/*
  Corporate gifting inquiry: three-step form in a <dialog>.
  - [data-cg-inquiry-open="<dialog id>"] opens the dialog at step 1.
  - Continue validates the visible step before moving on.
  - Submit posts the Shopify contact form natively so Shopify's hCaptcha can add
    its token; the page then reopens the dialog on success or on step 3 with errors.
*/
(function () {
  if (window.cgInquiryReady) return;
  window.cgInquiryReady = true;

  function today() {
    var d = new Date();
    var month = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + month + '-' + day;
  }

  function stepsOf(dialog) {
    return Array.prototype.slice.call(dialog.querySelectorAll('[data-step]'));
  }

  function showStep(dialog, name) {
    var current;
    stepsOf(dialog).forEach(function (step) {
      var match = step.getAttribute('data-step') === String(name);
      step.hidden = !match;
      if (match) current = step;
    });
    dialog.setAttribute('data-current-step', name);
    var panel = dialog.querySelector('.cg-inquiry__panel');
    if (panel) panel.scrollTop = 0;
    var title = current && current.querySelector('.cg-inquiry__title');
    if (title) title.focus({ preventScroll: true });
  }

  function currentStep(dialog) {
    return dialog.querySelector('[data-step]:not([hidden])');
  }

  function validate(step) {
    var fields = step.querySelectorAll('input, select, textarea');
    for (var i = 0; i < fields.length; i++) {
      if (!fields[i].disabled && !fields[i].checkValidity()) {
        fields[i].reportValidity();
        return false;
      }
    }
    return true;
  }

  function showError(dialog, message) {
    var error = dialog.querySelector('[data-cg-inquiry-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function reset(dialog) {
    var form = dialog.querySelector('form');
    if (form) form.reset();
    var date = dialog.querySelector('[data-cg-inquiry-date]');
    if (date) date.disabled = false;
    var note = dialog.querySelector('[data-cg-inquiry-custom-note]');
    if (note) note.hidden = true;
    showError(dialog, '');
    showStep(dialog, 1);
  }

  function open(dialog, fromTrigger) {
    var step = dialog.getAttribute('data-current-step');
    if (fromTrigger && step === 'success') reset(dialog);
    else if (!step) showStep(dialog, 1);
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('cg-inquiry-open');
  }

  function close(dialog) {
    if (dialog.open) dialog.close();
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  // Submits through a real submit event so Shopify's captcha script can attach
  // its token (form.submit() and fetch skip it: "Missing CAPTCHA token").
  // Deferred because browsers ignore a submit requested from inside a submit event.
  // The modal is closed first, or it would cover the hCaptcha challenge popup.
  // The page reloads; resolves false so the success step isn't shown early.
  function sendToShopify(form) {
    setTimeout(function () {
      var dialog = form.closest('dialog');
      if (dialog) close(dialog);
      form.setAttribute('data-cg-inquiry-native', '');
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else if (form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))) {
        HTMLFormElement.prototype.submit.call(form);
      }
    });
    return Promise.resolve(false);
  }

  // Sends the inquiry to Klaviyo as an event (Client API, public key only).
  // A Klaviyo flow triggered by this metric emails the team.
  function sendToKlaviyo(form, publicKey, metricName) {
    var answers = {};
    new FormData(form).forEach(function (value, key) {
      var match = /^contact\[(.+)\]$/.exec(key);
      if (!match || match[1] === 'Inquiry' || value === '') return;
      answers[match[1]] = String(value);
    });

    var name = (answers.name || '').trim();
    var space = name.indexOf(' ');
    var profile = {
      email: answers.email,
      first_name: space === -1 ? name : name.slice(0, space),
      last_name: space === -1 ? '' : name.slice(space + 1),
    };
    if (answers.Company) profile.organization = answers.Company;

    // Readable property names for the flow email, e.g. {{ event.Budget_per_gift }}.
    var properties = { Source: window.location.href };
    Object.keys(answers).forEach(function (key) {
      var label = { name: 'Name', email: 'Email', phone: 'Phone', body: 'Notes' }[key] || key;
      properties[label.replace(/[^A-Za-z0-9]+/g, '_')] = answers[key];
    });

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(publicKey), {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: '2024-10-15',
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: properties,
            metric: { data: { type: 'metric', attributes: { name: metricName } } },
            profile: { data: { type: 'profile', attributes: profile } },
          },
        },
      }),
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
    });
  }

  function submit(dialog, form) {
    var button = form.querySelector('[data-cg-inquiry-submit]');
    var klaviyoKey = (dialog.getAttribute('data-klaviyo-key') || '').trim();
    var metricName = dialog.getAttribute('data-klaviyo-metric') || 'Corporate Gifting Inquiry';
    var alsoStoreEmail = dialog.getAttribute('data-store-email') !== 'false';
    showError(dialog, '');
    setBusy(button, true);

    var request;
    if (!klaviyoKey) {
      request = sendToShopify(form);
    } else {
      request = sendToKlaviyo(form, klaviyoKey, metricName).then(
        function () {
          return alsoStoreEmail ? sendToShopify(form) : true;
        },
        function () {
          // Klaviyo unreachable: keep the lead by sending it to the store email instead.
          return sendToShopify(form);
        }
      );
    }

    request
      .then(function (done) {
        if (done) showStep(dialog, 'success');
      })
      .catch(function () {
        showError(dialog, 'Something went wrong. Please try again or email sales@brandinitoffee.com.');
      })
      .finally(function () {
        setBusy(button, false);
      });
  }

  function init(dialog) {
    if (dialog.hasAttribute('data-cg-inquiry-ready')) return;
    dialog.setAttribute('data-cg-inquiry-ready', '');

    var form = dialog.querySelector('form');
    var date = dialog.querySelector('[data-cg-inquiry-date]');
    var unsure = dialog.querySelector('[data-cg-inquiry-date-unsure]');
    var project = dialog.querySelector('[data-cg-inquiry-project]');
    var note = dialog.querySelector('[data-cg-inquiry-custom-note]');

    if (date) date.min = today();

    if (unsure && date) {
      unsure.addEventListener('change', function () {
        date.disabled = unsure.checked;
        if (unsure.checked) date.value = '';
      });
    }

    if (project && note) {
      project.addEventListener('change', function () {
        note.hidden = !/custom/i.test(project.value);
      });
    }

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-cg-inquiry-close]')) {
        close(dialog);
        return;
      }
      if (event.target.closest('[data-cg-inquiry-next]')) {
        var step = currentStep(dialog);
        if (!step || !validate(step)) return;
        showStep(dialog, Number(step.getAttribute('data-step')) + 1);
      }
    });

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('cg-inquiry-open');
    });

    if (form) {
      form.addEventListener('submit', function (event) {
        // Second pass from sendToShopify: let the browser and Shopify's captcha handle it.
        if (form.hasAttribute('data-cg-inquiry-native')) {
          form.removeAttribute('data-cg-inquiry-native');
          return;
        }
        // Hold Shopify's captcha listener back until our own work (Klaviyo) is done.
        event.preventDefault();
        event.stopImmediatePropagation();
        var step = currentStep(dialog);
        if (step && !validate(step)) return;
        submit(dialog, form);
      });
    }

    // Reopen after a full-page submit (success or errors).
    var state = dialog.querySelector('[data-cg-inquiry-state]');
    var openStep = state && state.getAttribute('data-open-step');
    if (openStep) {
      showStep(dialog, openStep);
      open(dialog);
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState(null, '', url.toString());
      }
    }
  }

  // Any link ending in this hash opens the form, e.g. "#corporate-inquiry" on the
  // same page or "/pages/corporate-gifting#corporate-inquiry" from another page.
  var OPEN_HASH = '#corporate-inquiry';

  function openFirst() {
    var dialog = document.querySelector('.cg-inquiry');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    init(dialog);
    open(dialog, true);
    return true;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-cg-inquiry-open], a[href$="' + OPEN_HASH + '"]');
    if (!trigger) return;

    if (trigger.hasAttribute('data-cg-inquiry-open')) {
      var dialog = document.getElementById(trigger.getAttribute('data-cg-inquiry-open'));
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      init(dialog);
      open(dialog, true);
      return;
    }

    // Open the form if it is on this page; otherwise the browser follows the link.
    if (openFirst()) event.preventDefault();
  });

  function initAll() {
    document.querySelectorAll('.cg-inquiry').forEach(init);
    if (window.location.hash === OPEN_HASH && openFirst() && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
